import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { ApiClientOptions } from './api/client';
import { App } from './app/App';
import './index.css';
import './lists.css';
import './variables.css';
import './activity.css';
import './deployments.css';
import './cloudAccounts.css';
import './designer.css';
import './networks-page.css';
import './networks-lists.css';
import './networks-deployment.css';
import './networks-phase2.css';
import './networks-entry-points.css';
import './networks-runs.css';
import './responsive.css';

async function apiOptions(): Promise<ApiClientOptions> {
  // `npm run dev:mock` runs the UI against the in-memory mock API; the mock is not part of normal builds.
  if (import.meta.env.MODE === 'mock') {
    const { createDemoMockApi, enableRevealDemo, MockApi } = await import('./api/mock/mockApi');
    const params = new URLSearchParams(window.location.search);
    // `?mock=empty` starts without data, to see the empty states.
    const api = params.get('mock') === 'empty' ? new MockApi({ runs: 'auto' }) : createDemoMockApi();
    // Reveal is off by default, as in the API; `?reveal=on|reason|norole` turns it on for the demo.
    const reveal = params.get('reveal');
    if (reveal === 'on' || reveal === 'reason' || reveal === 'norole') enableRevealDemo(api, reveal);
    // `?vault-check=fail`: re-reads of a soft-deleted vault fail ("couldn't check"); `?role=reader`: a reader.
    if (params.get('vault-check') === 'fail')
      api.vaultCheckError = { code: 'ACCESS_DENIED', message: 'deletedVaults/read is not granted.' };
    if (params.get('role') === 'reader') api.roles = ['reader'];
    // Networking (feature spec 17) is on in the demo; `?networking=off` shows the app with its gate off.
    if (params.get('networking') === 'off') api.net.enabled = false;
    // `?role=network-admin` / `operator`: one of the networking roles alone.
    if (params.get('role') === 'network-admin') api.roles = ['reader', 'network-admin'];
    if (params.get('role') === 'operator') api.roles = ['reader', 'operator'];
    return { fetch: api.fetch };
  }
  return { baseUrl: import.meta.env.VITE_API_BASE_URL ?? '' };
}

void apiOptions().then((options) =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App apiOptions={options} />
    </StrictMode>,
  ),
);
